/**
 * Údržba (zámek CMS pro klienty). Server source of truth:
 * `GET /api/maintenance/` (public) + any 503 `{maintenance:true}` from a write
 * (qcms/middleware/maintenance.py). Fetched on app load and every 60 s by
 * `plugins/maintenance.client.ts`; components read `useMaintenance()`.
 */
import { useAuthStore } from '~/store/auth'
import { getApiBase } from '~/utils/api-base'

export interface MaintenanceState {
  enabled: boolean
  message: string
  until: string | null
}

export function useMaintenanceState() {
  return useState<MaintenanceState>('qcms-maintenance', () => ({ enabled: false, message: '', until: null }))
}

export function useMaintenance() {
  const state = useMaintenanceState()
  const authStore = useAuthStore()
  const isAdmin = computed(() => !!authStore.user?.is_admin_role)
  // Clients are locked; admins only see a pill.
  const locked = computed(() => state.value.enabled && !isAdmin.value)
  const untilLabel = computed(() => formatUntil(state.value.until))
  return { state, isAdmin, locked, untilLabel }
}

export function formatUntil(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const time = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
  const sameDay = d.toDateString() === new Date().toDateString()
  return sameDay ? time : `${d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' })} ${time}`
}

/** Refresh from the public endpoint. Takes the state ref (callers outside a Nuxt
 * context — timers, the fetch hook — cannot call useState). `fetchImpl` = the
 * unpatched fetch. */
export async function refreshMaintenance(state: Ref<MaintenanceState>, fetchImpl: typeof fetch = fetch): Promise<void> {
  try {
    const res = await fetchImpl(`${getApiBase()}/api/maintenance/`)
    if (!res.ok) return
    const data = (await res.json()) as MaintenanceState
    state.value = { enabled: !!data.enabled, message: data.message || '', until: data.until || null }
  } catch {
    /* offline / API down: keep the last known state */
  }
}
